import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

import GetStartedLayout from './GetStartedLayout'
import {
  GS,
  getStartedPrimaryBtnSx,
  getStartedSecondaryBtnSx,
} from './getStartedTheme'
import {
  loadRegistration,
  markRegistrationVerified,
} from '../../utils/onboardingStorage'
import {
  persistControlPlaneSession,
  resendVerificationApi,
  verifyEmailApi,
} from '../../api/onboardingApi'

const VerifyEmail: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const token = searchParams.get('token')
  const [registration, setRegistration] = useState(loadRegistration())
  const [verifying, setVerifying] = useState(Boolean(token))
  const [verified, setVerified] = useState(false)
  const [verifyError, setVerifyError] = useState<string | null>(null)
  const [resending, setResending] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)

  useEffect(() => {
    if (token) return
    if (!registration) {
      navigate('/get-started', { replace: true })
    } else if (registration.emailVerified) {
      navigate('/get-started/welcome', { replace: true })
    }
  }, [token, registration, navigate])

  useEffect(() => {
    if (!token) return

    let active = true
    const run = async () => {
      setVerifying(true)
      setVerifyError(null)
      try {
        const result = await verifyEmailApi(token)
        // Always persist — Strict Mode remount must not drop a successful verify.
        persistControlPlaneSession(result)
        const updated = markRegistrationVerified()
        if (active) {
          if (updated) setRegistration(updated)
          setVerified(true)
          setTimeout(
            () => navigate('/get-started/welcome', { replace: true }),
            1200
          )
        } else {
          navigate('/get-started/welcome', { replace: true })
        }
      } catch (err: any) {
        if (active) {
          const raw = String(
            err.response?.data?.message || err.message || ''
          )
          setVerifyError(
            /password policy not met/i.test(raw)
              ? t(
                  'getStarted.verifyEmail.passwordPolicyFailed',
                  'Your password does not meet security requirements. Go back to registration and choose a stronger password (12+ characters with upper/lowercase, a number, and @$!%*?&).'
                )
              : raw || t('getStarted.verifyEmail.verifyFailed', 'Verification failed.')
          )
        }
      } finally {
        if (active) setVerifying(false)
      }
    }
    run()
    return () => {
      active = false
    }
  }, [token, navigate])

  const handleResend = async () => {
    if (!registration?.professionalEmail) return
    setResending(true)
    setResendSuccess(false)
    try {
      await resendVerificationApi(registration.professionalEmail)
      setResendSuccess(true)
    } catch (err: any) {
      setVerifyError(
        err.response?.data?.message ||
          err.message ||
          'Could not resend email.'
      )
    } finally {
      setResending(false)
    }
  }

  if (!registration && !token) return null

  if (verifying) {
    return (
      <GetStartedLayout
        step={2}
        title={t(
          'getStarted.verifyEmail.verifyingTitle',
          'Verifying your email…'
        )}
        maxWidth={520}
      >
        <Stack alignItems="center" spacing={2} py={3}>
          <CircularProgress sx={{ color: GS.teal }} />
          <Typography
            sx={{ fontFamily: GS.font, fontSize: 16, color: GS.muted }}
          >
            {t(
              'getStarted.verifyEmail.verifyingSubtitle',
              'Activating your organization — this takes a moment.'
            )}
          </Typography>
        </Stack>
      </GetStartedLayout>
    )
  }

  if (verified) {
    return (
      <GetStartedLayout
        step={2}
        title={t(
          'getStarted.verifyEmail.confirmedTitle',
          'Email confirmed!'
        )}
        maxWidth={520}
      >
        <Stack alignItems="center" spacing={2} py={3}>
          <CheckCircleOutlineIcon sx={{ fontSize: 56, color: GS.teal }} />
          <Typography
            sx={{
              fontFamily: GS.font,
              fontSize: 16,
              color: GS.muted,
              textAlign: 'center',
            }}
          >
            {t(
              'getStarted.verifyEmail.confirmedSubtitle',
              'Your account and company are ready. Opening your welcome page…'
            )}
          </Typography>
        </Stack>
      </GetStartedLayout>
    )
  }

  const displayEmail = registration?.professionalEmail || ''
  const nextSteps = [
    {
      title: t(
        'getStarted.verifyEmail.stepOpenTitle',
        'Open the email'
      ),
      body: t(
        'getStarted.verifyEmail.stepOpenBody',
        'Look for a message from INCYIA in your inbox (and spam).'
      ),
    },
    {
      title: t(
        'getStarted.verifyEmail.stepClickTitle',
        'Click the confirmation link'
      ),
      body: t(
        'getStarted.verifyEmail.stepClickBody',
        'That activates your account and creates your organization space.'
      ),
    },
    {
      title: t(
        'getStarted.verifyEmail.stepExploreTitle',
        'Explore packs'
      ),
      body: t(
        'getStarted.verifyEmail.stepExploreBody',
        'You’ll land on welcome to compare Standard, Business and Premium.'
      ),
    },
  ]

  return (
    <GetStartedLayout
      step={2}
      title={t('getStarted.verifyEmail.pendingTitle', 'Check your inbox')}
      subtitle={t(
        'getStarted.verifyEmail.pendingSubtitle',
        'One click in your email activates your organization.'
      )}
      maxWidth={560}
    >
      <Stack spacing={2.75}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            borderRadius: '12px',
            bgcolor: GS.tealSoft,
            border: '1px solid #B5DFD7',
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              bgcolor: '#fff',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <MarkEmailReadOutlinedIcon
              sx={{ fontSize: 26, color: GS.tealDeep }}
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: GS.font,
                fontSize: 13,
                fontWeight: 600,
                color: GS.tealDeep,
                mb: 0.25,
              }}
            >
              {t('getStarted.verifyEmail.sentTo', 'An email was sent to')}
            </Typography>
            <Typography
              sx={{
                fontFamily: GS.font,
                fontSize: 16,
                fontWeight: 700,
                color: GS.text,
                wordBreak: 'break-word',
              }}
            >
              {displayEmail || '—'}
            </Typography>
          </Box>
        </Box>

        <Stack spacing={1.25}>
          {nextSteps.map((step, index) => (
            <Box
              key={step.title}
              sx={{
                display: 'flex',
                gap: 1.5,
                p: 1.75,
                borderRadius: '10px',
                border: `1px solid ${GS.lineSoft}`,
                bgcolor: GS.surface2,
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  bgcolor: '#fff',
                  border: `1px solid ${GS.line}`,
                  color: GS.navy,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  fontFamily: GS.font,
                  fontWeight: 700,
                  fontSize: 13,
                }}
                aria-hidden
              >
                {index + 1}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: GS.font,
                    fontSize: 14.5,
                    fontWeight: 700,
                    color: GS.text,
                    mb: 0.25,
                  }}
                >
                  {step.title}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: GS.font,
                    fontSize: 13.5,
                    color: GS.muted,
                    lineHeight: 1.45,
                  }}
                >
                  {step.body}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>

        {verifyError && (
          <Alert severity="error" sx={{ fontFamily: GS.font }}>
            {verifyError}
          </Alert>
        )}

        {resendSuccess && (
          <Alert severity="success" sx={{ fontFamily: GS.font }}>
            {t('getStarted.verifyEmail.resent', 'Verification email resent.')}
          </Alert>
        )}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          width="100%"
        >
          <Button
            variant="contained"
            disabled={resending || !displayEmail}
            onClick={handleResend}
            sx={{ ...getStartedPrimaryBtnSx, flex: 1 }}
          >
            {resending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('getStarted.verifyEmail.resend', 'Resend email')
            )}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/get-started')}
            sx={{ ...getStartedSecondaryBtnSx, flex: 1 }}
          >
            {t('getStarted.back', 'Back')}
          </Button>
        </Stack>
      </Stack>
    </GetStartedLayout>
  )
}

export default VerifyEmail
